import i18n from '@/libs/i18n'
import type { ApiError } from '@/types/Common'

/** Mirrors the error codes signage-api answers with (docs/API.md → Errors). */
export enum Errors {
    InternalServerError = 1,
    InvalidData         = 2,
    NotFound            = 3,
    Unauthorized        = 4,
    FailedToInsert      = 5,
    Forbidden           = 6,
    UsernameTaken       = 7,
    LastAdmin           = 8,
    PasswordTooShort    = 9,
    WrongPassword       = 10,
    TooManyAttempts     = 11,
    PasskeysUnavailable = 12,
    SlugTaken           = 13,
    PairingCodeInvalid  = 14,
    FileTooLarge        = 15,
    UnsupportedMedia    = 16,
    InUse               = 17,
    TenantInactive      = 18,
    WeatherUnavailable  = 19,
    AlreadyMember       = 20,
    PosNotConfigured    = 21,
    PosFailed           = 22,
    ShareCodeInvalid    = 23,
    ShareSessionEnded   = 24,
    DomainTaken         = 25,
}

/** The code of an API error body, or 0 when it carries none. */
export const errorCode = (errorObject: unknown): number => {
    const error = (errorObject as ApiError)?.error

    return typeof error === 'object' && error ? error.code ?? 0 : 0
}

/** The server's own message of an API error body, trimmed. */
export const errorMessage = (errorObject: unknown): string => {
    const error = (errorObject as ApiError)?.error

    if (typeof error === 'string') return error.trim()

    return (error?.message ?? '').trim()
}

/*
 * parseError turns an API error body into a sentence.
 *
 * The web translates by CODE, never by message (docs/API.md → Errors): the server's messages
 * are English and meant for a log. A code this console has no sentence for gets the caller's
 * fallback, which names what was being attempted.
 */
export const parseError = (errorObject: unknown, fallback = ''): string => {
    const t = i18n.global.t

    switch (errorCode(errorObject)) {
        case Errors.InvalidData:     return t('errors.invalid_data')
        case Errors.NotFound:        return t('errors.not_found')
        case Errors.Unauthorized:    return t('errors.unauthorized')
        case Errors.Forbidden:       return t('errors.forbidden')
        case Errors.TooManyAttempts: return t('auth.too_many_attempts')
        case Errors.TenantInactive:  return t('errors.tenant_inactive')
        default:                     return fallback || t('errors.generic_error')
    }
}

/*
 * parseKnownError is parseError plus the refusals a person can actually cause — a taken
 * username or slug, somebody already a member of the tenant, the last administrator, a short or wrong password, a wrong pairing code,
 * a file that is too big or of the wrong kind, something still in use, a POS that is not connected
 * or did not answer, a share code that does not exist (or whose screen does not take shares), a share
 * that is already over, or a look's share address that another look or a tenant already uses.
 * "In use" (17) keeps the server's message: it is the list of names that use the thing, the only
 * part worth reading. So does "POS failed" (22): its message is the POS's
 * HTTP status and short reason (never the token), which is what support asks for.
 */
export const parseKnownError = (errorObject: unknown, fallback = ''): string => {
    const t = i18n.global.t

    switch (errorCode(errorObject)) {
        case Errors.UsernameTaken:       return t('user.username_taken')
        case Errors.LastAdmin:           return t('user.last_admin')
        case Errors.PasswordTooShort:    return t('user.password_too_short')
        case Errors.WrongPassword:       return t('password.wrong_current')
        case Errors.PasskeysUnavailable: return t('passkey.unavailable')
        case Errors.SlugTaken:           return t('tenant.slug_taken')
        case Errors.PairingCodeInvalid:  return t('screen.pair_invalid')
        case Errors.FileTooLarge:        return t('media.too_large')
        case Errors.UnsupportedMedia:    return t('media.unsupported')
        case Errors.WeatherUnavailable:  return t('errors.weather_unavailable')
        case Errors.AlreadyMember:       return t('user.already_member')
        case Errors.PosNotConfigured:    return t('errors.pos_not_configured')
        case Errors.ShareCodeInvalid:    return t('errors.share_code_invalid')
        case Errors.ShareSessionEnded:   return t('errors.share_session_ended')
        case Errors.DomainTaken:         return t('errors.domain_taken')
        case Errors.PosFailed: {
            const reason = errorMessage(errorObject)

            return reason ? t('errors.pos_failed_reason', { reason }) : t('errors.pos_failed')
        }
        case Errors.InUse: {
            const list = errorMessage(errorObject)

            return list ? t('errors.in_use_list', { list }) : t('errors.in_use')
        }
        default:                         return parseError(errorObject, fallback)
    }
}
